/**
 * Signing out turns notifications off on this phone, so the next person on a shared phone
 * never sees "Your talk is tomorrow at 10:00". Every sign-out calls
 * turnOffPushOnSignOut() BEFORE it removes the tokens: clearSession() in utils/api.js
 * (Log out, forced sign-out, the daily sign-out), and the Log out handlers in
 * UserContext.js and LandingPage.js.
 *
 * It never holds up the sign-out: the server hears about it through a keepalive request
 * that outlives the page, and the browser drops its subscription in the background. If
 * the page leaves first, the next page load finishes the job (finishSignOut).
 *
 * This file must not import utils/api.js, which imports it.
 */

// The endpoint of this browser's subscription and the server it was sent to
const DEVICE_KEY = "annaPush.device";
// A sign-out whose browser-side half hasn't finished yet
const PENDING_KEY = "annaPush.signOutPending";

const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (error) {
    return null;
  }
};
const write = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (error) {
    // Storage blocked: the subscription is still dropped below.
  }
};
const remove = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (error) {
    // Nothing kept to remove
  }
};

export const rememberDevice = (endpoint, server) => write(DEVICE_KEY, JSON.stringify({ endpoint, server }));
export const forgetDevice = () => remove(DEVICE_KEY);
const readDevice = () => {
  try {
    const device = JSON.parse(read(DEVICE_KEY) || "null");
    return device && typeof device.endpoint === "string" && typeof device.server === "string" ? device : null;
  } catch (error) {
    return null;
  }
};

let cleanup = Promise.resolve();
let busy = false;
/** Resolves once a sign-out's unsubscribe is done, so a new subscription isn't dropped by it. */
export const signOutCleanup = () => cleanup;
/** True while that unsubscribe is still running. */
export const signOutBusy = () => busy;

async function dropBrowserSubscription() {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = registration?.pushManager ? await registration.pushManager.getSubscription() : null;
  if (subscription) await subscription.unsubscribe();
  if (navigator.clearAppBadge) await navigator.clearAppBadge().catch(() => {});
}

const dropInBackground = () => {
  busy = true;
  cleanup = dropBrowserSubscription()
    .then(() => remove(PENDING_KEY))
    .catch(() => {})
    .finally(() => {
      busy = false;
    });
  return cleanup;
};

/** Call while the tokens still exist. Returns at once and never throws. */
export function turnOffPushOnSignOut() {
  try {
    const device = readDevice();
    const token = read("authToken");
    if (device && token && typeof fetch === "function") {
      // Without the token (already signed out) the server drops the endpoint the
      // first time its push service answers 404/410.
      try {
        fetch(`${device.server}/api/push/subscription/`, {
          method: "DELETE",
          keepalive: true,
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-Tenant-Domain": window.location.hostname,
          },
          body: JSON.stringify({ endpoint: device.endpoint }),
        }).catch(() => {});
      } catch (error) {
        // The browser's half below still runs.
      }
    }
    forgetDevice();
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    write(PENDING_KEY, "1");
    dropInBackground();
  } catch (error) {
    // Signing out must go on whatever happens here.
  }
}

/** On page load: finish a sign-out whose page left before the browser dropped the subscription. */
export function finishSignOut() {
  if (read(PENDING_KEY) === "1") dropInBackground();
}
